import { cx } from 'class-variance-authority';
import { usePathname } from 'next/navigation';
import { type FocusEventHandler, useState } from 'react';

import { EventType } from '@/analytics/types';

import PopAccuracyComfortSectionBg from './PopAccuracyComfortSectionBg';
import PopAccuracyComfortSectionContent from './PopAccuracyComfortSectionContent';
import PopAccuracyComfortSectionVideoModal from './PopAccuracyComfortSectionVideoModal';
import type { PopAccuracyComfortSectionData } from './types';

export interface PopAccuracyComfortSectionProps
  extends Omit<PopAccuracyComfortSectionData, 'id'> {
  readonly index: number;
  readonly isActive: boolean;
  readonly onFocus: FocusEventHandler<HTMLElement>;
}

const PopAccuracyComfortSection = ({
  contentIntroTransitionDelay,
  highlights,
  index,
  isActive,
  onFocus,
  theme,
  title,
  titleClassName = '',
  shouldScrollAnimateBg,
  videoBg,
  videoCta,
  videoFeature,
}: PopAccuracyComfortSectionProps) => {
  const [isSectionVisible, setIsSectionVisible] = useState<boolean>(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);
  const path = usePathname();

  const handleSectionVisibilityChange = (visible: boolean) => {
    setIsSectionVisible(visible);
  };

  const toggleVideoModal = () => {
    setIsVideoModalOpen((isOpen) => !isOpen);
  };

  const onVideoCtaClick = () => {
    void window.ouraAnalytics.track(EventType.ModuleClicked, {
      ...videoCta.analyticsProperties,
      path,
    });

    setIsVideoModalOpen(true);
  };

  return (
    <section
      className={cx(
        'overflow-hidden md:h-screen md:w-full md:motion-reduce:relative md:nojs:relative md:motion-safe:js:absolute md:motion-safe:js:top-0',
        {
          'text-gray-600': theme.sm === 'light',
          'text-sandstone-200': theme.sm === 'dark',
          'md:text-gray-600': theme.md === 'light',
          'md:text-sandstone-200': theme.md === 'dark',
          'md:motion-safe:pointer-events-none': !isActive,
        },
      )}
      data-cy="pop-accuracy-comfort-section"
      onFocus={onFocus}
    >
      <PopAccuracyComfortSectionBg
        index={index}
        contentIntroTransitionDelay={contentIntroTransitionDelay}
        isActive={isActive}
        onSectionVisibilityChange={handleSectionVisibilityChange}
        shouldScrollAnimateBg={shouldScrollAnimateBg}
        theme={theme}
        videoBg={videoBg}
      />
      <PopAccuracyComfortSectionContent
        highlights={highlights}
        index={index}
        contentIntroTransitionDelay={contentIntroTransitionDelay}
        isSectionVisible={isSectionVisible}
        title={title}
        titleClassName={titleClassName}
        videoCta={{
          label: videoCta.label,
          onClick: onVideoCtaClick,
        }}
      />
      <PopAccuracyComfortSectionVideoModal
        isOpen={isVideoModalOpen}
        toggleModal={toggleVideoModal}
        video={videoFeature}
      />
    </section>
  );
};

export default PopAccuracyComfortSection;
